import { module } from 'stereomodularjs';

export default class extends module {
    constructor(m) {
        super(m);
        this.container = null;
        this.originalText = '';
        this.resizeObserver = null;
        this.delay = this.el.dataset.lineDelay || 0;
        this.stagger = this.el.dataset.lineStagger || 0;
    }

    init() {
        this.container = this.el;
        if (!this.container) return;

        this.originalText = this.container.innerHTML.trim();
        this.split_into_lines();
        this.delay_lines();

        // ResizeObserver for container size changes
        this.resizeObserver = new ResizeObserver(() => {
            this.split_into_lines();
            this.delay_lines();
        });
        this.resizeObserver.observe(this.container);

        // Fallback for window resize events
        this.handleWindowResize = () => {
            this.split_into_lines();
            this.delay_lines();
        };
        window.addEventListener('resize', this.handleWindowResize);
    }

    split_into_lines() {
        if (!this.container) return;

        // First, split the text by <br> tags to handle explicit line breaks
        const lineSegments = this.originalText.split(/<br\s*\/?>/i);
        
        // Clear the container
        this.container.innerHTML = '';
        
        // Process each line segment separately
        lineSegments.forEach((segment, segmentIndex) => {
            if (segment.trim() === '') {
                // Handle empty segments (just <br> tags)
                const emptyLine = this.create_span(['']);
                this.container.appendChild(emptyLine);
                return;
            }

            // Create a temporary container for this segment
            const tempContainer = document.createElement('div');
            tempContainer.style.position = 'absolute';
            tempContainer.style.visibility = 'hidden';
            tempContainer.style.whiteSpace = 'nowrap';
            this.container.appendChild(tempContainer);

            // Replace each <strong>…</strong> block with individual <strong>word</strong> tags
            const textWithIndividualStrongs = segment.replace(
                /<strong>([\s\S]*?)<\/strong>/g,
                (_, inner) => inner
                    .split(' ')
                    .map(word => `<strong>${word.replace(/<\/?strong>/g, '')}</strong>`)
                    .join(' ')
            );

            // Split on spaces
            const raw_chunks = textWithIndividualStrongs.split(' ');
            let words = [];
            let word_types = [];

            raw_chunks.forEach(chunk => {
                if (chunk.includes('-')) {
                    // Check if this chunk is wrapped in strong tags
                    const isStrong = chunk.startsWith('<strong>') && chunk.endsWith('</strong>');
                    const cleanChunk = chunk.replace(/<\/?strong>/g, '');
                    
                    const parts = cleanChunk.split('-');

                    parts.forEach((part, index) => {
                        const is_last = index === parts.length - 1;
                        const full_part = is_last ? part : part + '-';
                        if (full_part) {
                            // Preserve strong tags for hyphenated words
                            const wordWithTags = isStrong ? `<strong>${full_part}</strong>` : full_part;
                            words.push(wordWithTags);
                            word_types.push(true); // this is part of a hyphenated word
                        }
                    });
                } else {
                    words.push(chunk);
                    word_types.push(false); // regular word
                }
            });

            // Create temporary spans for this segment
            let temp_spans = [];
            words.forEach((word, index) => {
                const temp_span = document.createElement('span');

                // Ajouter &#8200; *seulement* si ce n'est pas un mot lié par un tiret
                const should_add_space = !word_types[index] && index !== words.length - 1;
                const should_add_another_space = word_types[index] == true && word_types[index + 1] == false;

                temp_span.innerHTML = word + (should_add_space ? '&#8200;' : '') + (should_add_another_space ? '&#8200;' : '');
                tempContainer.appendChild(temp_span);
                temp_spans.push(temp_span);
            });

            // Detect lines for this segment
            let last_offset_top = temp_spans[0]?.offsetTop;
            let current_line = [];
            let segment_lines = [];

            temp_spans.forEach((span, index) => {
                let offset_top = span.offsetTop;

                if (offset_top !== last_offset_top) {
                    if (current_line.length > 0) {
                        segment_lines.push(this.create_span(current_line));
                    }
                    current_line = [];
                    last_offset_top = offset_top;
                }

                current_line.push(span.innerHTML);
            });

            // Add the last line of this segment
            if (current_line.length > 0) {
                segment_lines.push(this.create_span(current_line));
            }

            // Remove the temporary container
            this.container.removeChild(tempContainer);

            // Add all lines from this segment to the main container
            segment_lines.forEach(line => {
                this.container.appendChild(line);
            });
        });

        // Clean up the final HTML
        // remove &#8200; and replace with a space
        this.container.innerHTML = this.container.innerHTML.replace(/&#8200;/g, ' ');
        // remove spaces after the last word of each line
        this.container.innerHTML = this.container.innerHTML.replace(/(\s+)(<\/span>)/g, '$2');
    }

    create_span(words) {
        // Crée un conteneur parent
        const parentSpan = document.createElement('span');
        parentSpan.classList.add('line');
        parentSpan.innerHTML = words.join(''); // Enlever si on veut double wrap

        // // Crée un span enfant
        // const childSpan = document.createElement('span');
        // childSpan.classList.add('split-line');
        // childSpan.innerHTML = words.join('');

        // // Ajoute le span enfant au conteneur parent
        // parentSpan.appendChild(childSpan);

        return parentSpan;
    }

    delay_lines() {
        // Ajoute un délai à chaque ligne
        this.el.querySelectorAll('.line').forEach((line, index) => {
            line.style.transitionDelay = `${this.delay + index * this.stagger}s`;
        });
    }

    destroy() {
        // Disconnect ResizeObserver
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        // Remove window resize event listener
        if (this.handleWindowResize) {
            window.removeEventListener('resize', this.handleWindowResize);
            this.handleWindowResize = null;
        }

        // Restore original content
        this.container.innerHTML = this.originalText;
    }
}
